import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule, FormControlName } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { SetFloatingFilter } from '../../@shared/set-floating-filter.component';
import { BillingService } from '../../@services/billing.service';
import { format_date_string } from '../../@shared/helpers';

@Component({
  selector: 'app-theo-trade-billing',
  imports: [AgGridAngular, CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './theo-trade-billing-external.component.html',
  styleUrl: './theo-trade-billing-external.component.scss'
})
export class TheoTradeBillingExternalComponent {
  subs: any = [];
  gridDarkTheme = agGridThemes.dark;
  theme: any;
  roles: any[] = [];

  grid: any;
  data: any[] = [];
  filtered_data: any[] = [];
  selected_user: any;
  include_last_login: boolean = false;

  tracking_grid: any;
  tracking_data: any[] = [];
  filtered_tracking_data: any[] = [];
  selected_tracking_user: any;

  revenue_rows: any[] = [];
  ghost_user_rows: any[] = [];
  legacy_user_rows: any[] = [];
  revenue_month_keys: string[] = [];
  user_month_keys: string[] = [];
  missing_order_logs: any = [];

  constructor(private billingService : BillingService) {
    this.init_grid();
    this.init_tracking_grid();
    this.theme = this.gridDarkTheme;
  }

  async ngOnInit() {
    this.get_theotrade_billing();
  }

  ngOnDestroy(): void {}

  // Get billing report
  async get_theotrade_billing() {
    this.selected_user = null;
    let res: any = await this.billingService.getTheoTradeBillingExternal(this.include_last_login);

    this.missing_order_logs = res.missing_orders.map((l: any) => {
      let start_date = format_date_string(l.start_date);
      let end_date = (l.end_date) ? format_date_string(l.end_date) : null;
      let last_login = l.last_login ? format_date_string(l.last_login) : '';

      return {
        email: l.email,
        scanner: l.scanner,
        start_date,
        end_date,
        last_login
      }
    });

    this.data = res.orders.map((o: any) => {
      let order_date = format_date_string(o.date);
      let refund = o.refund ?? null;

      return {
        id: o.id,
        date: o.date,
        email: o.email,
        scanner: o.scanner,
        product_name: o.product,
        product_type: o.product_type,
        price: o.price,
        order_date,
        refund
      }
    });

    // Generate column headers for each month in revenue report
    const all_revenue_months = new Set<string>();
    Object.values(res.revenue).forEach((scanner: any) => {
      Object.keys(scanner.months).forEach(month => all_revenue_months.add(month));
    });
    this.revenue_month_keys = Array.from(all_revenue_months).sort();

    // Create table rows for revenue data
    const scanners = Object.keys(res.revenue);
    this.revenue_rows = scanners.map((scanner: any) => {
      const row: any = { scanner };

      this.revenue_month_keys.forEach(month => {
        const entry = res.revenue[scanner].months[month];
        if (entry) {
          row[month] = `$${entry.revenue.toLocaleString()} (${entry.count})`;
        } else {
          row[month] = "–";
        }
      });

      return row;
    });

    // Sort revenue table rows so totals appear last
    this.revenue_rows.sort((a: any, b: any) => {
      if (a.scanner === "All Scanners") return 1;
      if (b.scanner === "All Scanners") return -1;
      return 0;
    });

    // Get tracking report
    this.tracking_data = res.tracking;

    // Generate column headers for each month in lifetime users report
    const all_user_months = new Set<string>();
    Object.values(res.ghost_users).forEach((scanner: any) => {
      Object.keys(scanner.months).forEach(month => all_user_months.add(month));
    });
    this.user_month_keys = Array.from(all_user_months).sort();

    // Create rows for legacy users
    const legacy_user_scanners = Object.keys(res.legacy_users);
    this.legacy_user_rows = legacy_user_scanners.map((scanner: any) => {
      const row: any = { scanner };

      this.user_month_keys.forEach((month: any) => {
        const entry = res.legacy_users[scanner].months[month];
        if (entry) {
          row[month] = entry;
        } else {
          row[month] = "–";
        }
      });

      return row;
    });

    // Create rows for phantom users with no order history
    const ghost_user_scanners = Object.keys(res.ghost_users);
    console.log(res.ghost_users);

    this.ghost_user_rows = ghost_user_scanners.map((scanner: any) => {
      const row: any = { scanner };

      this.user_month_keys.forEach((month: any) => {
        const entry = res.ghost_users[scanner].months[month];
        if (entry) {
          row[month] = entry;
        } else {
          row[month] = "–";
        }
      });

      return row;
    });
  }

  async clear_filters() {
    // Clear grid filters
    if (this.grid.api) {
      this.grid.api.setFilterModel(null);
    }
  }

  format_month(input: string) {
    if (input === 'Total') return 'Total';

    const [year_string, month_string] = input.split('-');
    const year = parseInt(year_string, 10);
    const month = parseInt(month_string, 10) - 1;
    const date = new Date(year, month, 1);

    return date.toLocaleString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  }

  download_csv(data: any[], name: any) {
    const csv = this.billingService.generateCSV(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.csv`;
    a.click();

    window.URL.revokeObjectURL(url);
  }

  /**** Orders Grid ****/
  init_grid() {
  
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          return params.data.id;
        },
        onRowClicked: this.on_row_clicked.bind(this),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          field: "id",
          headerName: 'Identifer',
          hide: true,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "order_date",
          headerName: 'Order Date',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "email",
          headerName: 'Email',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "scanner",
          headerName: 'Scanner',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "product_name",
          headerName: 'Product',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "product_type",
          headerName: 'Type',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "price",
          headerName: 'Total ($)',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          valueFormatter: (params: any) => {
            return params.value?.toLocaleString('en-US');
          }
        },
        {
          field: "refund",
          headerName: 'Refund ($)',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          valueFormatter: (params: any) => {
            return params.value?.toLocaleString('en-US');
          }
        },
      ]
    }
  }
  on_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid?.api) {
      this.filtered_data = [];
      this.grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_data.push(node.data);
      });
    }
  }
  on_row_selected(e: any) {
    // console.log('on_row_selected', e);
    if (e?.node?.isSelected() === true) {
      this.selected_user = e.node.data;
    }

    //Force refresh of scanners column to show edit button
    this.grid.api.refreshCells({
      columns: ['attributes.custom:scanners'],
      force: true
    });
  }
  on_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let user = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        case 'edit-scanners':
          //this.on_edit_user_scanners(user);
          break;
        default:
          break;
      }
    }
  }


  /**** User Tracking Grid ****/
  init_tracking_grid() {
    this.tracking_grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          return params.data.id;
        },
        onRowClicked: this.on_row_clicked.bind(this),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          field: "id",
          headerName: 'Identifer',
          hide: true,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "email",
          headerName: 'Email',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "scanner",
          headerName: 'Scanner',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "num_orders",
          headerName: 'Orders',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "total_spent",
          headerName: 'Total Spent ($)',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          valueFormatter: (params: any) => {
            return params.value?.toLocaleString('en-US');
          }
        },
        {
          field: "start_date",
          headerName: 'Start Date',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['start_date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "end_date",
          headerName: 'End Date',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['end_date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "last_order_date",
          headerName: 'Last Order Date',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['last_order_date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "last_order_total",
          headerName: 'Last Order Total',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          valueFormatter: (params: any) => {
            return params.value?.toLocaleString('en-US');
          }
        },
        {
          field: "last_order_product",
          headerName: 'Last Order Product',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "last_order_type",
          headerName: 'Last Order Type',
          minWidth: 100,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "last_login",
          headerName: 'Last Login',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['last_login'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
      ]
    }
  }
  on_tracking_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.tracking_grid.api = e.api;
    this.tracking_grid.colApi = e.colApi;
    this.tracking_grid.api.sizeColumnsToFit();
  }
  on_tracking_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.tracking_grid?.api) {
      this.filtered_tracking_data = [];
      this.tracking_grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_tracking_data.push(node.data);
      });
    }
  }
  on_tracking_row_selected(e: any) {
    // console.log('on_row_selected', e);
    if (e?.node?.isSelected() === true) {
      this.selected_tracking_user = e.node.data;
    }
  }
  on_tracking_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let user = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        case 'edit-scanners':
          //this.on_edit_user_scanners(user);
          break;
        default:
          break;
      }
    }
  }
}
